import PaymentIcon from "@mui/icons-material/Payment";
import useExcelUrl from "app/useExcelUrl";
import {
  DataGridV2 as DataGrid,
  DataGridBaseBaseProps,
  DataGridModalEditColumn,
  enumToOptions,
  GridColumns,
  PageHeader,
} from "common";
import Dialog from "components/DraggableDialog/DraggableDialog";
import { BOOKING_STATUSES } from "features/bookings/bookingConstants";
import BookingForm from "features/bookings/BookingForm";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { useState } from "react";
import { PaymentTransaction } from "./paymentTransactionApi";
import { PAYMENT_TRANSACTION_STATUS } from "./paymentTransactionConstants";
import PaymentTransactionForm from "./PaymentTransactionForm";
import usePaymentTransactionList from "./usePaymentTransactionList";

export interface PaymentTransactionListProps extends Omit<
  DataGridBaseBaseProps,
  "id"
> {
  showHeader?: boolean;
}

export default function PaymentTransactionList({
  showHeader = true,
  ...props
}: PaymentTransactionListProps) {
  const { params, scope, editUrl, ...service } = usePaymentTransactionList();
  const { hasPermissions, canEditItem } = useAccess();
  const excelUrl = useExcelUrl("payment-transactions");
  const [bookingModalId, setBookingModalId] = useState<number | null>(null);

  let columns: GridColumns<PaymentTransaction> = [
    {
      field: "id",
      headerName: I18n.t("PaymentTransaction.attributes.id"),
      renderCell: params => (
        <DataGridModalEditColumn
          href={`${editUrl}${params.row?.id}`}
          params={{ ...params, value: `#${params.row?.id}` }}
        />
      ),
    },
    {
      field: "booking_number",
      headerName: I18n.t("PaymentTransaction.attributes.booking_number"),
      type: "number",
      renderCell: params => (
        <DataGridModalEditColumn
          params={{
            ...params,
            value: params.row?.booking?.booking_number || params.value,
          }}
          onClick={() => {
            if (params.row?.booking_id) {
              setBookingModalId(params.row.booking_id);
            }
          }}
        />
      ),
    },
    {
      field: "booking.status",
      headerName: I18n.t("PaymentTransaction.attributes.booking_status"),
      type: "singleSelect",
      valueOptions: enumToOptions(BOOKING_STATUSES, "Booking.options.status"),
    },
    {
      field: "billing_name",
      headerName: I18n.t("PaymentTransaction.attributes.billing_name"),
    },
    {
      field: "billing_email",
      headerName: I18n.t("PaymentTransaction.attributes.billing_email"),
    },
    {
      field: "status",
      headerName: I18n.t("PaymentTransaction.attributes.status"),
      type: "singleSelect",
      valueOptions: enumToOptions(
        PAYMENT_TRANSACTION_STATUS,
        "PaymentTransaction.options.status",
      ),
    },
    {
      field: "gateway",
      headerName: I18n.t("PaymentTransaction.attributes.gateway"),
    },
    {
      field: "amount",
      headerName: I18n.t("PaymentTransaction.attributes.amount"),
      type: "number",
    },
    {
      field: "currency",
      headerName: I18n.t("PaymentTransaction.attributes.currency"),
    },
    {
      field: "invoiced",
      headerName: I18n.t("PaymentTransaction.attributes.invoiced"),
      type: "boolean",
    },
    {
      field: "paid_at",
      headerName: I18n.t("PaymentTransaction.attributes.paid_at"),
      type: "dateTime",
    },
    {
      field: "expires_at",
      headerName: I18n.t("PaymentTransaction.attributes.expires_at"),
      type: "dateTime",
    },
    {
      field: "created_at",
      headerName: I18n.t("PaymentTransaction.attributes.created_at"),
      type: "dateTime",
    },
  ];

  return (
    <>
      {showHeader && (
        <PageHeader
          avatar={<PaymentIcon />}
          title={I18n.t("PaymentTransaction.title")}
        />
      )}
      <DataGrid
        {...service}
        columns={columns}
        id="payment-transactions"
        sections={[
          {
            value: "invoiceNeeded",
            label: I18n.t("PaymentTransaction.sections.invoice_needed"),
          },
          {
            value: "failed",
            label: I18n.t("PaymentTransaction.sections.failed"),
          },
          {
            value: "other",
            label: I18n.t("PaymentTransaction.sections.other"),
          },
          { value: "", label: I18n.t("PaymentTransaction.sections.all") },
        ]}
        defaultState={{
          sorting: [{ field: "created_at", direction: "desc" }],
        }}
        excelUrl={excelUrl}
        renderModalForm={props => <PaymentTransactionForm {...props} />}
        modalProps={{ maxWidth: "sm" }}
        {...props}
        //getHeaderName={field => I18n.t(`PaymentTransaction.attributes.${field.replaceAll(".", "_")}`)}
        canCreate={hasPermissions("payment_transaction.create")}
        /*canDelete={hasPermissions([
          "payment_transaction.editany",
          "payment_transaction.editown",
        ])}*/
        canEdit={hasPermissions([
          "payment_transaction.editany",
          "payment_transaction.editown",
        ])}
        canDeleteRow={params => canEditItem("payment_transaction", params.row)}
        canEditRow={params => canEditItem("payment_transaction", params.row)}
        canBatchActions={hasPermissions([
          "payment_transaction.editany",
          "payment_transaction.editown",
        ])}
        /*canHandleTrash={hasPermissions([
          "payment_transaction.editany",
          "payment_transaction.editown",
        ])}*/
      />
      {bookingModalId && (
        <Dialog
          open={!!bookingModalId}
          onClose={() => setBookingModalId(null)}
          maxWidth="sm"
          fullWidth
        >
          <BookingForm
            id={String(bookingModalId)}
            onClose={() => setBookingModalId(null)}
            afterSave={() => {
              setBookingModalId(null);
            }}
          />
        </Dialog>
      )}
    </>
  );
}
